import { CHARACTERS, SHOP, STAGES, passiveById, spellById } from '../game/content';
import { BESTIARY_ENEMIES, EVOLUTION_RECIPES, bountyMilestone } from '../game/codex';
import { isOn, supported } from '../core/Fullscreen';
import { canInstall, watchInstall } from '../core/Install';
import { MAX_STORM_TIER, affordableUpgrade, mageMastery, mainLevel, shopCost, stormMultipliers } from '../game/rules';
import { DAILY_BOUNTY_GOLD, getDailyTrial, mutatorById, todayUtcString } from '../game/daily';
import { gearIcon, gearItemIcon, passiveIcon, shopIcon, spellIcon } from './icons';
import { CHESTS, GEAR_CAP, GEAR_SLOTS, MAX_GEAR_LEVEL, RARITIES, activePerks, activeSets, chestPrice, emptyGear, enhanceCost, fuseCandidates, gearBase, gearSet, itemPerk, itemStats, pityLeft, reforgeCost, salvageValue, type GearItem, type GearSlot, type GearStat, type GearState } from '../game/gear';
import type { SaveData, Settings } from '../game/Save';
import { charText, digits, enemyText, formatGearStat, gearName, getLang, num, passiveText, percent, shopText, spellDesc, spellName, stageText, t, type Lang } from '../i18n';
import type { UiKey } from '../i18n/en.ts';
import type { Account, DailyStatus, Leaderboard } from '../net/Cloud';

export interface UpgradeOption {
  kind: 'spell' | 'passive' | 'gold' | 'heal';
  id: string;
  name: string;
  icon: string;
  color: string;
  tag: string;
  tagClass: '' | 'new' | 'evo';
  desc: string;
  hint?: string;
}

export interface SlotView {
  icon: string;
  color: string;
  level: string;
  evolved?: boolean;
  ready?: boolean;
  title: string;
}

export interface ArmoryView {
  tab: 'equipment' | 'chests';
  portrait: string;
  damage: number;
  maxHp: number;
}

export interface UiHandlers {
  onAction(action: string): void;
  onSelectCharacter(id: string): void;
  onSelectStage(index: number): void;
  onSelectStormTier?(tier: number): void;
  onBuy(id: string): void;
  onClaimBounty?(enemyId: string): void;
  onChest(id: string, count: 1 | 10): void;
  onItem(itemId: string): void;
  onSettings(settings: Settings): void;
  onLanguage(lang: Lang): void;
  onPick(index: number): void;
  onReroll(): void;
  onBanish?(): void;
  onLock?(): void;
  onAuth(mode: AuthMode, email: string, password: string, name: string): void;
  onSignOut(): void;
  onLeaderboardStage(stage: string): void;
}

export type AuthMode = 'login' | 'register';

// Server error codes → messages. Unknown codes fall back to a generic message.
const AUTH_ERRORS: Record<string, UiKey> = {
  email: 'account.err.email',
  password: 'account.err.password',
  name: 'account.err.name',
  email_taken: 'account.err.emailTaken',
  name_taken: 'account.err.nameTaken',
  bad_credentials: 'account.err.credentials',
  rate_limited: 'account.err.rate',
  network: 'account.err.network',
  expired: 'account.err.expired',
  telegram_unavailable: 'account.err.telegram',
  telegram_taken: 'account.err.telegramTaken',
  already_linked: 'account.err.alreadyLinked',
};

const TG_ICON = '<svg class="tg-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.5 3.6 2.9 10.8c-1.3.5-1.3 1.2-.2 1.6l4.8 1.5 1.8 5.6c.2.6.4.8.9.8.4 0 .6-.2.9-.5l2.3-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3.1-14.6c.3-1.3-.5-1.9-1.4-1.5Zm-3 3.6-8.9 8-.4 3.9-1.6-5.2 10.4-6.6c.5-.3.9-.1.5.1Z"/></svg>';

type Screen = 'title' | 'select' | 'shop' | 'armory' | 'settings' | 'howto' | 'leaderboard';

const CHEST_COLORS: Record<string, string> = { bronze: '#d08a4e', silver: '#c9d3e0', gold: '#ffd24d' };

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element ${sel}`);
  return el;
};

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export const formatTime = (s: number) => digits(`${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`);

export class Ui {
  private readonly screens: Record<Screen, HTMLElement> = {
    title: $('#screen-title'),
    select: $('#screen-select'),
    shop: $('#screen-shop'),
    armory: $('#screen-armory'),
    settings: $('#screen-settings'),
    howto: $('#screen-howto'),
    leaderboard: $('#screen-leaderboard'),
  };
  private readonly hudEl = $('#hud');
  private readonly dashButton = $('#dash-button');
  private readonly xpFill = $('#xp-fill');
  private readonly levelLabel = $('#level-label');
  private readonly levelBadge = $('#level-badge');
  private readonly timer = $('#timer');
  private readonly timerPlate = $('#timer-plate');
  private readonly stageFill = $('#stage-fill');
  private readonly kills = $('#kills');
  private readonly pace = $('#pace');
  private readonly gold = $('#gold');
  private readonly hpFill = $('#hp-fill');
  private readonly hpLabel = $('#hp-label');
  private readonly dashFill = $('#dash-fill');
  private readonly bossBar = $('#boss-bar');
  private readonly bossName = $('#boss-name');
  private readonly bossFill = $('#boss-fill');
  private readonly banner = $('#banner');
  private readonly bannerTitle = $('#banner-title');
  private readonly bannerSub = $('#banner-sub');
  private readonly hurtFlash = $('#hurt-flash');
  private readonly screenFlash = $('#screen-flash');
  private readonly levelModal = $('#modal-levelup');
  private readonly levelCards = $('#levelup-cards');
  private readonly rerollButton = $<HTMLButtonElement>('#reroll-button');
  private readonly banishButton = $<HTMLButtonElement>('#banish-button');
  private readonly lockButton = $<HTMLButtonElement>('#lock-button');
  private readonly pauseModal = $('#modal-pause');
  private readonly resultsModal = $('#modal-results');
  private readonly accountModal = $('#modal-account');
  private readonly itemModal = $('#modal-item');
  private readonly gearModal = $('#modal-gear');
  private authMode: AuthMode = 'login';
  private toastTimer = 0;
  private slotKey = '';
  private lastHud = { level: -1, kills: -1, gold: -1, time: -1 };
  private picking = false;
  private shopTab: 'upgrades' | 'evolutions' | 'bestiary' = 'upgrades';
  private lastArmoryView: ArmoryView = { tab: 'equipment', portrait: '', damage: 0, maxHp: 100 };
  private lastGear: GearState = emptyGear();
  private lastInspect: { id: string; level: number } | null = null;

  private daily: DailyStatus | null = null;
  private dailyDate = '';

  constructor(handlers: UiHandlers) {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const actionEl = target.closest<HTMLElement>('[data-action]');
      if (actionEl) {
        if (actionEl.classList.contains('locked')) return;
        const action = actionEl.dataset.action!;
        if (action === 'armory-tab') {
          const tab = (actionEl.dataset.tab as 'equipment' | 'chests') ?? 'equipment';
          this.lastArmoryView = { ...this.lastArmoryView, tab };
          this.screens.armory.querySelector<HTMLElement>('.panel-body')?.scrollTo(0, 0);
          handlers.onAction(`armory-tab:${tab}`);
          return;
        }
        if (action === 'shop-tab') {
          this.shopTab = (actionEl.dataset.tab as 'upgrades' | 'evolutions' | 'bestiary') ?? 'upgrades';
          this.screens.shop.querySelector<HTMLElement>('.panel-body')?.scrollTo(0, 0);
          this.syncShopTabs();
          return;
        }
        handlers.onAction(action);
        return;
      }
      const char = target.closest<HTMLElement>('[data-char]');
      if (char && !char.classList.contains('locked')) handlers.onSelectCharacter(char.dataset.char!);
      const stage = target.closest<HTMLElement>('[data-stage]');
      if (stage && !stage.classList.contains('locked')) handlers.onSelectStage(Number(stage.dataset.stage));
      const storm = target.closest<HTMLElement>('[data-storm-tier]');
      if (storm) handlers.onSelectStormTier?.(Number(storm.dataset.stormTier));
      const buy = target.closest<HTMLElement>('[data-buy]');
      if (buy && !buy.classList.contains('locked')) handlers.onBuy(buy.dataset.buy!);
      const bounty = target.closest<HTMLElement>('[data-bounty]');
      if (bounty && bounty.classList.contains('claimable')) handlers.onClaimBounty?.(bounty.dataset.bounty!);
      const chest = target.closest<HTMLElement>('[data-chest]');
      if (chest && !chest.classList.contains('locked')) {
        const count = Number(chest.dataset.count) === 10 ? 10 : 1;
        handlers.onChest(chest.dataset.chest!, count);
        return;
      }
      const item = target.closest<HTMLElement>('[data-item]');
      if (item && !this.gearOpen) {
        handlers.onItem(item.dataset.item!);
        return;
      }
      const pick = target.closest<HTMLElement>('[data-pick]');
      if (pick && this.picking) handlers.onPick(Number(pick.dataset.pick));
    });
    this.rerollButton.addEventListener('click', () => handlers.onReroll());
    this.banishButton.addEventListener('click', () => handlers.onBanish?.());
    this.lockButton.addEventListener('click', () => handlers.onLock?.());
    document.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      if (form.id !== 'account-form') return;
      event.preventDefault();
      const value = (name: string) => (form.elements.namedItem(name) as HTMLInputElement | null)?.value ?? '';
      handlers.onAuth(this.authMode, value('email'), value('password'), value('name'));
    });
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const mode = target.closest<HTMLElement>('[data-auth-mode]')?.dataset.authMode as AuthMode | undefined;
      if (mode && mode !== this.authMode) {
        this.authMode = mode;
        this.showAccount(null);
      }
      if (target.closest('[data-action="sign-out"]')) handlers.onSignOut();
      const lb = target.closest<HTMLElement>('[data-lb-stage]')?.dataset.lbStage;
      if (lb) handlers.onLeaderboardStage(lb);
    });
    $('#pause-button').addEventListener('click', () => handlers.onAction('pause'));
    window.addEventListener('keydown', (event) => {
      if (!this.picking || event.repeat) return; // a held key would pick cards the player never saw
      const n = Number(event.key);
      if (n >= 1 && n <= 4) handlers.onPick(n - 1);
      if (event.code === 'KeyR') handlers.onReroll();
      if (event.code === 'KeyB') handlers.onBanish?.();
      if (event.code === 'KeyL') handlers.onLock?.();
    });
    const settingsInputs = ['#set-quality', '#set-sfx', '#set-music', '#set-shake', '#set-numbers', '#set-joystick', '#set-lefthanded', '#set-haptics'];
    for (const sel of settingsInputs) $(sel).addEventListener('input', () => handlers.onSettings(this.readSettings()));
    $<HTMLSelectElement>('#set-language').addEventListener('change', (event) => handlers.onLanguage((event.target as HTMLSelectElement).value as Lang));
    // No context menu over the game, but keep it in text fields (paste on phones).
    $('#app').addEventListener('contextmenu', (event) => {
      if (!(event.target as HTMLElement).closest('input, textarea')) event.preventDefault();
    });
    document.addEventListener('fullscreenchange', () => this.syncFullscreen());
    document.addEventListener('webkitfullscreenchange', () => this.syncFullscreen()); // Safari before 16.4
    this.syncFullscreen();
    watchInstall(() => this.syncInstall());
    this.syncInstall();
    window.setInterval(() => {
      if (!this.screens.title.classList.contains('hidden')) this.tickDaily();
    }, 1000);
    this.renderDaily(null);
  }

  // --- menus ---

  show(screen: Screen | null): void {
    for (const [name, el] of Object.entries(this.screens)) el.classList.toggle('hidden', name !== screen);
    if (screen) {
      this.screens[screen].querySelector<HTMLElement>('.panel-body')?.scrollTo(0, 0);
      (this.screens[screen].querySelector<HTMLElement>('.btn.primary') ?? undefined)?.focus({ preventScroll: true });
    }
  }

  renderTitle(save: Pick<SaveData, 'gold' | 'mainXp' | 'shop' | 'cleared'>): void {
    $('#title-gold').textContent = t('title.gold', { n: save.gold });
    // A gold dot on the Grimoire while the player can afford a rank: most players who never bought one had the gold.
    const ready = !!affordableUpgrade(save.shop, save.gold);
    $('#title-shop').classList.toggle('ready', ready);
    $('#title-shop').title = ready ? t('menu.shopReady') : '';
    // The Daily runs at Storm Tier 2 in any realm, so it waits until the first clear instead of greeting beginners.
    $('#title-daily').classList.toggle('hidden', save.cleared === 0);
    const main = mainLevel(save.mainXp);
    $('#title-level-text').textContent = t('title.level', { n: num(main.level) });
    const xpRatio = Math.min(1, Math.max(0, main.into / main.need));
    $('#title-xp-fill').style.transform = `scaleX(${xpRatio})`;
    $('#title-level').title = t('title.levelXp', { into: num(main.into), need: num(main.need) });
  }

  // Title-screen Daily Trial button: streak (signed in) and the time left in today's trial, which ends at UTC midnight.
  renderDaily(status: DailyStatus | null): void {
    this.daily = status;
    this.dailyDate = todayUtcString();
    // One short line: the title screen has no height to spare on phones. The full streak wording is the tooltip.
    const streak = status && status.streak > 0 ? `<span class="ds-streak" title="${esc(t('daily.streak', { n: status.streak }))}">🔥 ${esc(num(status.streak))}</span>` : '';
    $('#title-daily').innerHTML = `<span class="ds-name"><span class="glyph gold" aria-hidden="true">✦</span> ${esc(t('daily.short'))}</span>${streak}<span class="ds-time" id="title-daily-time"></span>`;
    this.tickDaily();
  }

  private tickDaily(): void {
    // Past midnight the trial is a new one: today's is not done yet, and a streak still alive stays shown.
    if (this.dailyDate !== todayUtcString() && this.daily?.today) this.renderDaily({ ...this.daily, today: false });
    const now = new Date();
    const left = Math.max(0, Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1) - now.getTime()) / 1000));
    const time = digits([Math.floor(left / 3600), Math.floor(left / 60) % 60, left % 60].map((n) => String(n).padStart(2, '0')).join(':'));
    const done = !!this.daily?.today;
    const el = $('#title-daily-time');
    el.textContent = t(done ? 'daily.nextIn' : 'daily.endsIn', { time });
    $('#title-daily').classList.toggle('done', done);
  }

  // Re-renders the text built in code after a language change; static text is handled by applyStatic().
  localize(save: SaveData): void {
    this.lastHud = { level: -1, kills: -1, gold: -1, time: -1 };
    this.hudCache.clear(); // the HP label is worded per language
    this.slotKey = '';
    this.renderTitle(save);
    this.renderDaily(this.daily);
    $('#level-caption').textContent = t('hud.level', { n: '' }).trim();
    if (!this.screens.select.classList.contains('hidden')) this.renderSelect(save);
    if (!this.screens.shop.classList.contains('hidden')) this.renderShop(save);
    if (!this.screens.armory.classList.contains('hidden')) this.renderArmory(save, this.lastArmoryView);
    this.syncFullscreen();
  }

  renderSelect(save: SaveData): void {
    $('#char-grid').innerHTML = CHARACTERS.map((c) => {
      const locked = save.cleared < c.unlockStage;
      const spell = spellById(c.spell);
      const text = charText(c.id);
      const mInfo = mageMastery(save.mastery?.[c.id] ?? 0);
      const mRank = mInfo.rank;
      const mPct = Math.min(1, Math.max(0, mInfo.into / Math.max(1, mInfo.need)));
      return `<button type="button" class="card char-card ${locked ? 'locked' : ''} ${save.character === c.id ? 'selected' : ''}" data-char="${c.id}" style="--c:${c.robe};--c2:${c.trim}">

        <div class="char-head"><div class="portrait"><img src="/ui/heroes/${c.id}.webp" alt="" width="64" height="64" loading="lazy" /></div><div class="char-badges">${mRank > 0 ? `<span class="badge mastery-badge">${esc(t('select.mastery', { n: num(mRank) }))}</span>` : ''}<span class="char-spell">${spellIcon(spell.id, spell.icon)}</span></div></div>
        <div class="name">${esc(text.name)}</div>
        <div class="meta">${esc(text.title)} · ${esc(spellName(spell))}</div>
        <div class="meta">${locked ? esc(t('select.lockedChar', { stage: stageText(STAGES[c.unlockStage - 1].id).name })) : esc(text.perk)}</div>
        ${!locked ? `<div class="mastery-track" aria-hidden="true"><i style="transform:scaleX(${mPct.toFixed(3)})"></i></div>` : ''}
      </button>`;
    }).join('');
    $('#stage-grid').innerHTML = STAGES.map((s, i) => {
      const locked = i > save.cleared;
      const cleared = i < save.cleared;
      const best = save.best[s.id];
      const text = stageText(s.id);
      return `<button type="button" class="card ${locked ? 'locked' : ''} ${save.stage === i ? 'selected' : ''}" data-stage="${i}">
        <div class="stage-swatch" style="--s1:${s.visuals.ground[1]};--s2:${s.visuals.accent}"><img src="/ui/stages/${s.id}.webp" alt="" width="240" height="135" loading="lazy" /></div>
        ${cleared ? `<span class="badge">${esc(t('select.cleared'))}</span>` : ''}
        <div class="name">${num(i + 1)}. ${esc(text.name)}</div>
        <div class="meta">${locked ? esc(t('select.lockedStage')) : esc(text.subtitle)}</div>
        ${best ? `<div class="meta">${esc(t('select.best', { time: formatTime(best) }))}</div>` : ''}
      </button>`;
    }).join('');
    const tier = save.stormTier ?? 0;
    const mult = stormMultipliers(tier);
    const tierDesc = tier === 0
      ? t('select.stormNormal')
      : t('select.stormDesc', {
          hp: num(Math.round((mult.hp - 1) * 100)),
          dmg: num(Math.round((mult.dmg - 1) * 100)),
          gold: num(Math.round(mult.goldBonus * 100)),
        });
    const tierButtons = Array.from({ length: MAX_STORM_TIER + 1 }, (_, k) =>
      `<button type="button" class="storm-chip ${tier === k ? 'on' : ''}" data-storm-tier="${k}">⚡ ${num(k)}</button>`,
    ).join('');
    $('#storm-tier-bar').innerHTML = `<div class="storm-head"><span class="storm-title">${esc(t('select.stormTier'))}</span><div class="storm-chips">${tierButtons}</div></div><div class="storm-meta">${esc(tierDesc)}</div>`;
    const daily = getDailyTrial();
    const dailyChar = charText(daily.characterId).name;
    const dailyStage = stageText(STAGES[daily.stageIndex].id).name;
    const claimed = save.dailyClaimed === daily.date;
    const mutChips = daily.mutators.map((id) => {
      const def = mutatorById(id);
      return `<span class="storm-chip daily-mut" style="--mc:${def.color}" title="${esc(t(`mutator.${id}.desc` as UiKey))}"><b>${esc(t(`mutator.${id}.name` as UiKey))}</b> · ${esc(t(`mutator.${id}.desc` as UiKey))}</span>`;
    }).join('');
    const bountyText = claimed ? t('daily.claimed') : t('daily.bounty', { n: num(DAILY_BOUNTY_GOLD) });
    $('#daily-trial-bar').innerHTML = `<div class="storm-head"><span class="storm-title">✦ ${esc(t('daily.title'))} · ${esc(dailyChar)} · ${esc(dailyStage)} (⚡ ${num(daily.stormTier)})</span><button type="button" class="btn primary daily-btn" data-action="daily-trial">${esc(t('daily.play'))}</button></div><div class="storm-chips">${mutChips}</div><div class="storm-meta">${esc(bountyText)}</div>`;
    // Footer recap of the current choice, so Begin always says what it starts.
    const c = CHARACTERS.find((ch) => ch.id === save.character && save.cleared >= ch.unlockStage) ?? CHARACTERS[0];
    $('#select-summary').innerHTML = `<b>${esc(charText(c.id).name)}</b> · ${esc(spellName(spellById(c.spell)))} · <b>${esc(stageText(STAGES[save.stage].id).name)}</b>${tier > 0 ? ` · ⚡ ${num(tier)}` : ''}`;
  }

  private syncShopTabs(): void {
    for (const btn of document.querySelectorAll<HTMLElement>('[data-action="shop-tab"]')) {
      const on = btn.dataset.tab === this.shopTab;
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-selected', String(on));
    }
    $('#shop-grid').classList.toggle('hidden', this.shopTab !== 'upgrades');
    $('#codex-evolutions').classList.toggle('hidden', this.shopTab !== 'evolutions');
    $('#codex-bestiary').classList.toggle('hidden', this.shopTab !== 'bestiary');
  }

  renderShop(save: SaveData): void {
    $('#shop-gold').textContent = num(save.gold);
    this.syncShopTabs();
    $('#shop-grid').innerHTML = SHOP.map((item) => {
      const rank = save.shop[item.id] ?? 0;
      const maxed = rank >= item.maxRank;
      const cost = shopCost(item, rank);
      const poor = !maxed && cost > save.gold;
      const pips = Array.from({ length: item.maxRank }, (_, i) => `<span class="pip ${i < rank ? 'on' : ''}"></span>`).join('');
      const text = shopText(item.id);
      return `<button type="button" class="card ${maxed || poor ? 'locked' : ''}" data-buy="${item.id}" style="--c:#ffd24d">
        <div class="name"><span class="ticon">${shopIcon(item.id, item.icon)}</span> ${esc(text.name)}</div>
        <div class="meta">${esc(t('shop.perRank', { desc: text.desc }))}</div>
        <div class="pips">${pips}</div>
        <div class="cost ${poor ? 'poor' : ''}">${maxed ? esc(t('shop.maxed')) : `◉ ${num(cost)}`}</div>
      </button>`;
    }).join('');
    $('#codex-evolutions').innerHTML = EVOLUTION_RECIPES.map((r) => {
      const spell = spellById(r.spellId);
      const passive = passiveById(r.passiveId);
      const discovered = !!save.evolutions?.[r.spellId];
      return `<div class="card static codex-card ${discovered ? 'discovered' : ''}" style="--c:${spell.evolution.color}">
        <div class="codex-recipe">
          <span class="slot small codex-slot" style="--c:${spell.color}">${spellIcon(spell.id, spell.icon)}</span>
          <span class="codex-plus">+</span>
          <span class="slot small codex-slot" style="--c:${passive.color}">${passiveIcon(passive.id, passive.icon)}</span>
          <span class="codex-plus">→</span>
          <span class="slot small codex-slot evolved" style="--c:${spell.evolution.color}">${spellIcon(spell.id, spell.icon)}</span>
        </div>
        <div class="name">${esc(spellName(spell))} + ${esc(passiveText(passive.id).name)}</div>
        <div class="meta"><b>${esc(spellName(spell, true))}</b> — ${esc(spellDesc(spell, true))}</div>
        <div class="tag ${discovered ? 'evo' : ''}">${esc(discovered ? t('codex.discovered') : t('codex.undiscovered'))}</div>
      </div>`;
    }).join('');
    $('#codex-bestiary').innerHTML = BESTIARY_ENEMIES.map((e) => {
      const kills = save.bestiary?.[e.id] ?? 0;
      const tier = save.bounties?.[e.id] ?? 0;
      const b = bountyMilestone(e.id, kills, tier);
      const text = enemyText(e.id);
      const pips = [0, 1, 2].map((i) => `<span class="pip ${i < tier ? 'on' : ''}"></span>`).join('');
      const pct = Math.min(1, Math.max(0, kills / Math.max(1, b.target)));
      return `<button type="button" class="card bestiary-card ${b.claimable ? 'claimable' : kills === 0 ? 'locked' : ''}" data-bounty="${e.id}" style="--c:${e.glow}">
        <div class="name">${esc(text.name)}</div>
        <div class="meta">${esc(t('codex.slain', { n: num(kills), max: num(b.target) }))}</div>
        <div class="bounty-track" aria-hidden="true"><i style="transform:scaleX(${pct.toFixed(3)})"></i></div>
        <div class="pips">${pips}</div>
        <div class="cost">${b.maxed ? esc(t('codex.claimed')) : b.claimable ? esc(t('codex.claim', { n: num(b.reward) })) : `◉ ${num(b.reward)}`}</div>
      </button>`;
    }).join('');
  }

  renderArmory(save: SaveData, view: ArmoryView = this.lastArmoryView): void {
    this.lastArmoryView = view;
    this.lastGear = save.gear;
    const { items, equipped } = save.gear;
    const worn = new Set(Object.values(equipped));
    const full = items.length >= GEAR_CAP;
    const free = GEAR_CAP - items.length;

    $('#armory-gold').textContent = num(save.gold);
    for (const btn of document.querySelectorAll<HTMLElement>('[data-action="armory-tab"]')) {
      const on = btn.dataset.tab === view.tab;
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-selected', String(on));
    }
    $('#armory-equipment').classList.toggle('hidden', view.tab !== 'equipment');
    $('#armory-chests').classList.toggle('hidden', view.tab !== 'chests');

    const slotHtml = (slot: GearSlot) => {
      const item = items.find((it) => it.id === equipped[slot]);
      if (item) return this.gearTile(item, true, true);
      return `<div class="gear-tile slot-tile empty" data-slot="${slot}" style="--c:#8a84a8">
        <div class="tile-icon-main">${gearIcon(slot)}</div>
        <span class="tile-name">${esc(t(`slot.${slot}`))}</span>
      </div>`;
    };
    $('#armory-slots-left').innerHTML = GEAR_SLOTS.slice(0, 3).map(slotHtml).join('');
    $('#armory-slots-right').innerHTML = GEAR_SLOTS.slice(3, 6).map(slotHtml).join('');

    const c = CHARACTERS.find((ch) => ch.id === save.character && save.cleared >= ch.unlockStage) ?? CHARACTERS[0];
    const spell = spellById(c.spell);
    const portraitInner = view.portrait
      ? `<img src="${view.portrait}" alt="${esc(charText(c.id).name)}" width="192" height="256" />`
      : `<div class="portrait">${spellIcon(spell.id, spell.icon)}</div>`;
    $('#armory-hero').innerHTML = `<div class="hero-frame" style="--c:${c.robe};--c2:${c.trim}">${portraitInner}<div class="hero-caption">${esc(charText(c.id).name)}</div></div>`;

    const dmgPct = Math.abs(view.damage) > 0 && Math.abs(view.damage) < 1 ? Math.round(view.damage * 100) : Math.round(view.damage);
    $('#armory-totals').innerHTML = `
      <span class="total-chip"><b>${esc(t('armory.damage', { v: dmgPct }))}</b></span>
      <span class="total-chip"><b>${esc(t('armory.maxHp', { v: Math.round(view.maxHp) }))}</b></span>`;
    this.renderArmoryBonuses(save.gear);

    $('#armory-count').textContent = t('armory.inventory', { n: items.length, max: GEAR_CAP }) + (full ? ` · ${t('armory.full')}` : '');
    const hasFreeCommon = items.some((it) => it.rarity === 0 && !worn.has(it.id));
    $('[data-action="salvage-commons"]').classList.toggle('locked', !hasFreeCommon);

    const sorted = [...items].sort((a, b) => b.rarity - a.rarity || b.level - a.level);
    $('#armory-items').innerHTML = sorted.length
      ? sorted.map((it) => this.gearTile(it, worn.has(it.id), false)).join('')
      : `<p class="meta armory-empty">${esc(t('armory.empty'))}</p>`;

    $('#armory-chests').innerHTML = CHESTS.map((chest) => {
      const odds = chest.odds.flatMap((p, r) => (p > 0 ? [`${t(`rarity.${RARITIES[r].id}`)} ${percent(Math.round(p * 100))}`] : [])).join(' · ');
      const lefts = pityLeft(chest, save.gear.pity?.[chest.id]);
      const guarantees = chest.guarantees
        .map((g, i) => {
          const left = lefts[i];
          const key = g.rarity === RARITIES.length - 1 ? 'armory.guaranteeTop' : 'armory.guarantee';
          return `<div class="chest-pity" style="--rc:${RARITIES[g.rarity].color}">${esc(t(key, { rarity: t(`rarity.${RARITIES[g.rarity].id}`), within: g.within, left }))}</div>`;
        })
        .join('');
      const buttons = ([1, 10] as const)
        .map((count) => {
          const price = chestPrice(chest, count);
          const noSpace = free < count;
          const poor = price > save.gold;
          const locked = noSpace || poor;
          const title = noSpace ? (count === 1 ? t('armory.full') : t('armory.needSpace', { n: count })) : '';
          return `<button type="button" class="btn chest-buy ${locked ? 'locked' : ''}" data-chest="${chest.id}" data-count="${count}" title="${esc(title)}">
            <span>${esc(t(count === 1 ? 'armory.x1' : 'armory.x10'))}</span>
            <span class="cost ${poor ? 'poor' : ''}">◉ ${num(price)}</span>
          </button>`;
        })
        .join('');
      return `<div class="card chest-card" style="--c:${CHEST_COLORS[chest.id]}">
        <div class="chest-art"><img src="/ui/chests/${chest.id}.webp" alt="" width="112" height="112" loading="lazy" /></div>
        <div class="chest-info">
          <div class="name">${esc(t(`chest.${chest.id}`))}</div>
          <div class="chest-guarantees">${guarantees}</div>
          <div class="meta">${esc(odds)}</div>
          <div class="chest-actions">${buttons}</div>
        </div>
      </div>`;
    }).join('');
  }

  private renderArmoryBonuses(gear: GearState): void {
    const chips: string[] = [];
    for (const { set, count, tiers } of activeSets(gear)) {
      if (tiers.length === 0) continue;
      const stats = set.bonuses
        .filter((b) => count >= b.pieces)
        .flatMap((b) => (Object.entries(b.stats) as Array<[GearStat, number]>).map(([s, v]) => formatGearStat(s, v)))
        .join(' · ');
      const label = t('armory.setChip', { name: t(`set.${set.id}` as UiKey), n: tiers[tiers.length - 1] });
      chips.push(`<span class="total-chip bonus-chip set-chip" data-set="${set.id}" style="--c:${set.color}"><b>${esc(label)}</b> · ${esc(stats)}</span>`);
    }
    for (const perk of activePerks(gear)) {
      const name = t(`perk.${perk}.name` as UiKey);
      const desc = t(`perk.${perk}.desc` as UiKey);
      chips.push(`<span class="total-chip bonus-chip perk-chip" data-perk="${perk}" title="${esc(desc)}"><b>★ ${esc(name)}</b> · ${esc(desc)}</span>`);
    }
    const el = $('#armory-bonuses');
    el.innerHTML = chips.join('');
    el.classList.toggle('hidden', chips.length === 0);
  }

  private itemBonusesHtml(item: GearItem): string {
    const base = gearBase(item.base)!;
    const set = gearSet(base.set);
    const count = activeSets(this.lastGear).find((s) => s.set.id === set.id)?.count ?? 0;
    const perk = itemPerk(item);
    const perkHtml = perk
      ? `<div class="desc item-perk" data-perk="${perk}">${esc(t('armory.legendaryPerk', { name: t(`perk.${perk}.name` as UiKey), desc: t(`perk.${perk}.desc` as UiKey) }))}</div>`
      : '';
    const tiersHtml = set.bonuses
      .map((b) => {
        const stats = (Object.entries(b.stats) as Array<[GearStat, number]>).map(([s, v]) => formatGearStat(s, v)).join(' · ');
        return `<div class="item-set-tier ${count >= b.pieces ? 'on' : ''}">${esc(t('armory.setBonus', { n: b.pieces, stats }))}</div>`;
      })
      .join('');
    return `${perkHtml}<div class="item-set" style="--sc:${set.color}">
      <div class="item-set-head">${esc(t('armory.setProgress', { name: t(`set.${set.id}` as UiKey), n: Math.min(4, count) }))}</div>
      ${tiersHtml}
    </div>`;
  }

  private gearTile(item: GearItem, worn: boolean, isSlot = false, idx?: number): string {
    const base = gearBase(item.base)!;
    const rarity = RARITIES[item.rarity];
    const idxStyle = idx !== undefined ? `;--i:${idx}` : '';
    return `<button type="button" class="gear-tile ${isSlot ? 'slot-tile' : ''} ${worn ? 'worn' : ''} rarity-${rarity.id}" data-item="${item.id}" data-set="${base.set}" style="--c:${rarity.color};--sc:${gearSet(base.set).color}${idxStyle}" title="${esc(gearName(base.id))} · ${esc(t(`set.${base.set}` as UiKey))}">
      <span class="tile-set" aria-hidden="true"></span>
      <span class="tile-slot">${gearIcon(base.slot)}</span>
      <span class="tile-lv">${esc(t('armory.level', { n: item.level }))}</span>
      <div class="tile-icon-main">${gearItemIcon(base.id)}</div>
      ${worn ? `<span class="tile-worn" aria-label="${esc(t('armory.worn'))}">✓</span>` : ''}
    </button>`;
  }

  showItem(item: GearItem, worn: boolean, gold: number, gear?: GearState): void {
    const enhanced = this.itemOpen && this.lastInspect?.id === item.id && item.level > this.lastInspect.level;
    this.lastInspect = { id: item.id, level: item.level };
    const base = gearBase(item.base)!;
    const rarity = RARITIES[item.rarity];
    const curStats = itemStats(item);
    const nextStats = item.level < MAX_GEAR_LEVEL ? new Map(itemStats({ ...item, level: item.level + 1 })) : null;
    const statsHtml = curStats
      .map(([stat, v]) => {
        const cur = formatGearStat(stat, v);
        const nxt = nextStats?.get(stat);
        const nextPart = nxt !== undefined ? ` <span class="stat-next">→ ${esc(t('armory.next'))}: ${esc(formatGearStat(stat, nxt))}</span>` : '';
        return `<div class="desc item-stat">${esc(cur)}${nextPart}</div>`;
      })
      .join('');
    $('#item-body').innerHTML = `<div class="upgrade static gear-reveal rarity-${rarity.id}" style="--c:${rarity.color}">
      <div class="icon">${gearItemIcon(base.id)}</div>
      <div class="name" id="item-title">${esc(gearName(base.id))}</div>
      <div><span class="tag">${esc(t(`rarity.${rarity.id}`))} · ${esc(t(`slot.${base.slot}`))} · ${esc(t('armory.level', { n: item.level }))}</span></div>
      ${statsHtml}
      ${this.itemBonusesHtml(item)}
    </div>`;
    const equipBtn = $('#item-equip-btn');
    if (enhanced && !reducedMotion()) {
      $('#item-body .icon').animate([{ transform: 'scale(1.14)' }, { transform: 'scale(1)' }], {
        duration: 220,
        easing: EASE_OUT,
      });
    }
    const salvageBtn = $('#item-salvage-btn');
    const reforgeBtn = $('#item-reforge-btn');
    const fuseBtn = $('#item-fuse-btn');
    const enhanceBtn = $('#item-enhance-btn');
    equipBtn.classList.toggle('hidden', worn);
    salvageBtn.classList.toggle('hidden', worn);
    salvageBtn.textContent = t('armory.salvage', { n: salvageValue(item) });
    const refCost = reforgeCost(item);
    reforgeBtn.textContent = t('armory.reforge', { n: refCost });
    reforgeBtn.classList.toggle('locked', refCost > gold);
    const nextRarity = RARITIES[item.rarity + 1];
    const canFuse = !worn && !!nextRarity && !!gear && fuseCandidates(gear, item).length >= 2;
    fuseBtn.classList.toggle('hidden', worn || !nextRarity);
    if (nextRarity) {
      fuseBtn.textContent = canFuse ? t('armory.fuse', { rarity: t(`rarity.${nextRarity.id}` as UiKey) }) : t('armory.fuseNeed');
      fuseBtn.classList.toggle('locked', !canFuse);
    }
    const cost = enhanceCost(item);
    if (cost === null) {
      enhanceBtn.textContent = t('armory.maxLevel');
      enhanceBtn.classList.add('locked');
    } else {
      enhanceBtn.textContent = t('armory.enhance', { n: cost });
      enhanceBtn.classList.toggle('locked', cost > gold);
    }
    this.itemModal.classList.remove('hidden');
    (this.itemModal.querySelector<HTMLElement>('.modal-foot > :not(.hidden):not(.locked)') ?? $('[data-action="item-close"]')).focus({ preventScroll: true });
  }

  hideItem(): void {
    this.itemModal.classList.add('hidden');
    this.lastInspect = null;
  }

  get itemOpen(): boolean {
    return !this.itemModal.classList.contains('hidden');
  }

  showReveal(items: GearItem[]): void {
    const single = items.length === 1;
    this.gearModal.classList.toggle('batch', !single);
    if (single) {
      const item = items[0];
      const base = gearBase(item.base)!;
      const rarity = RARITIES[item.rarity];
      $('#gear-reveal-card').innerHTML = `<div class="upgrade static gear-reveal rarity-${rarity.id}" style="--c:${rarity.color}">
        <div class="icon">${gearItemIcon(base.id)}</div>
        <div class="name">${esc(gearName(base.id))}</div>
        <div><span class="tag">${esc(t(`rarity.${rarity.id}`))} · ${esc(t(`slot.${base.slot}`))} · ${esc(t('armory.level', { n: item.level }))}</span></div>
        ${itemStats(item).map(([stat, v]) => `<div class="desc">${esc(formatGearStat(stat, v))}</div>`).join('')}
        ${this.itemBonusesHtml(item)}
      </div>`;
      $('#gear-salvage-btn').textContent = t('armory.salvage', { n: salvageValue(item) });
    } else {
      $('#gear-reveal-card').innerHTML = `<div class="gear-grid reveal-grid">${items.map((it, i) => this.gearTile(it, false, false, i)).join('')}</div>`;
      const hasCommon = items.some((it) => it.rarity === 0);
      $('#gear-salvage-commons-btn').classList.toggle('locked', !hasCommon);
    }
    $('#gear-salvage-btn').classList.toggle('hidden', !single);
    $('[data-action="gear-keep"]').classList.toggle('hidden', !single);
    $('[data-action="gear-equip"]').classList.toggle('hidden', !single);
    $('#gear-salvage-commons-btn').classList.toggle('hidden', single);
    $('#gear-keep-all-btn').classList.toggle('hidden', single);
    this.gearModal.classList.remove('hidden');
    this.gearModal.querySelector<HTMLElement>(single ? '[data-action="gear-equip"]' : '[data-action="gear-keep-all"]')?.focus({ preventScroll: true });
  }

  showGearReveal(item: GearItem): void {
    this.showReveal([item]);
  }

  hideGearReveal(): void {
    this.gearModal.classList.add('hidden');
  }

  get gearOpen(): boolean {
    return !this.gearModal.classList.contains('hidden');
  }

  renderSettings(s: Settings): void {
    $<HTMLSelectElement>('#set-quality').value = s.graphicsMode;
    $<HTMLSelectElement>('#set-language').value = s.lang;
    $<HTMLInputElement>('#set-sfx').value = String(s.sfx);
    $<HTMLInputElement>('#set-music').value = String(s.music);
    $<HTMLInputElement>('#set-shake').checked = s.shake;
    $<HTMLInputElement>('#set-numbers').checked = s.numbers;
    $<HTMLSelectElement>('#set-joystick').value = s.joystick;
    $<HTMLInputElement>('#set-lefthanded').checked = s.leftHanded;
    $<HTMLInputElement>('#set-haptics').checked = s.haptics;
  }

  private readSettings(): Settings {
    return {
      graphicsMode: $<HTMLSelectElement>('#set-quality').value as Settings['graphicsMode'],
      sfx: Number($<HTMLInputElement>('#set-sfx').value),
      music: Number($<HTMLInputElement>('#set-music').value),
      shake: $<HTMLInputElement>('#set-shake').checked,
      numbers: $<HTMLInputElement>('#set-numbers').checked,
      lang: getLang(),
      joystick: $<HTMLSelectElement>('#set-joystick').value as Settings['joystick'],
      leftHanded: $<HTMLInputElement>('#set-lefthanded').checked,
      haptics: $<HTMLInputElement>('#set-haptics').checked,
    };
  }

  // Install game shows only while the browser can install (or on iPhone, where it explains how) and the game isn't installed.
  syncInstall(): void {
    for (const el of document.querySelectorAll<HTMLElement>('[data-action="install"]')) el.classList.toggle('hidden', !canInstall());
  }

  showInstallHelp(open: boolean): void {
    $('#modal-install').classList.toggle('hidden', !open);
  }

  // Fullscreen buttons are hidden where the browser has no page fullscreen, and always show the action they perform.
  private syncFullscreen(): void {
    const label = t(isOn() ? 'common.exitFullscreen' : 'common.fullscreen');
    for (const el of document.querySelectorAll<HTMLElement>('[data-action="fullscreen"]')) {
      el.classList.toggle('hidden', !supported());
      if (el.classList.contains('icon-btn')) {
        el.setAttribute('aria-label', label);
        el.title = label;
      } else el.textContent = label;
    }
  }

  // --- HUD ---

  setHud(visible: boolean): void {
    this.hudEl.classList.toggle('hidden', !visible);
    $('#level-caption').textContent = t('hud.level', { n: '' }).trim();
    $('#touch-zone').classList.toggle('hidden', !visible);
    $('#stick').classList.toggle('hidden', !visible);
    this.dashButton.classList.toggle('hidden', !visible || !document.body.classList.contains('touch'));
    if (!visible) this.hurtFlash.classList.remove('low');
    this.lastHud = { level: -1, kills: -1, gold: -1, time: -1 };
  }

  updateHud(d: { time: number; progress: number; kills: number; pace: number | null; gold: number; level: number; xp: number; xpNext: number; hp: number; maxHp: number; dash: number; boss: { name: string; ratio: number } | null }): void {
    const secs = Math.floor(d.time);
    if (secs !== this.lastHud.time) this.timer.textContent = formatTime(secs);
    if (d.kills !== this.lastHud.kills) this.kills.textContent = num(d.kills);
    // -1e9: no best run to race (null), kept apart from a real delta of 0.
    // -1e9: no best run to race (null), kept apart from a real delta of 0.
    if (this.changed('pace', d.pace ?? -1e9)) {
      this.pace.hidden = d.pace === null;
      if (d.pace !== null) {
        this.pace.textContent = `${d.pace >= 0 ? '▲' : '▼'}${num(Math.abs(d.pace))}`;
        this.pace.title = t('hud.pace', { n: num(d.pace, { signDisplay: 'exceptZero' }) });
        this.pace.setAttribute('aria-label', this.pace.title);
        this.pace.classList.toggle('ahead', d.pace > 0);
        this.pace.classList.toggle('behind', d.pace < 0);
      }
    }
    if (d.gold !== this.lastHud.gold) this.gold.textContent = num(d.gold);
    if (d.level !== this.lastHud.level) {
      this.levelLabel.textContent = num(d.level);
      this.levelBadge.setAttribute('aria-label', t('hud.level', { n: d.level }));
      if (this.lastHud.level > 0 && !reducedMotion()) {
        this.levelBadge.animate([{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 240, easing: EASE_OUT });
      }
    }
    this.lastHud.level = d.level;
    this.lastHud.kills = d.kills;
    this.lastHud.gold = d.gold;
    this.lastHud.time = secs;
    const ratio = Math.max(0, d.hp / d.maxHp);
    this.bar('xp', d.xp / d.xpNext, this.xpFill);
    this.bar('hp', ratio, this.hpFill);
    const hp = Math.ceil(Math.max(0, d.hp));
    const max = Math.round(d.maxHp);
    if (this.changed('hpLabel', hp * 100000 + max)) this.hpLabel.textContent = t('hud.hp', { hp, max });
    this.hurtFlash.classList.toggle('low', ratio < 0.3 && d.hp > 0);
    this.bar('dash', d.dash, this.dashFill);
    // The touch button's conic ring repaints on every change, so it moves in 1/24 steps.
    const cd = Math.round(Math.min(1, Math.max(0, d.dash)) * 24);
    if (this.changed('cd', cd)) this.dashButton.style.setProperty('--cd', String(cd / 24));
    this.bar('stage', d.progress, this.stageFill);
    this.timerPlate.classList.toggle('boss', !!d.boss);
    this.bossBar.classList.toggle('hidden', !d.boss);
    if (d.boss) {
      if (d.boss.name !== this.bossName.textContent) this.bossName.textContent = d.boss.name;
      this.bar('boss', d.boss.ratio, this.bossFill);
    }
  }

  // Per-frame HUD values touch the DOM only when they change (every write costs style, layout or paint), and are
  // compared as numbers so an unchanged frame builds no strings: per-frame garbage means GC pauses on phones.
  private readonly hudCache = new Map<string, number>();
  private changed(key: string, value: number): boolean {
    if (this.hudCache.get(key) === value) return false;
    this.hudCache.set(key, value);
    return true;
  }

  // Bars scale on the compositor (transform), never width: a width change costs layout and paint every frame.
  private bar(key: string, ratio: number, el: HTMLElement): void {
    const v = Math.round(Math.min(1, Math.max(0, ratio)) * 1000);
    if (this.changed(key, v)) el.style.transform = `scaleX(${v / 1000})`;
  }

  setSlots(spells: SlotView[], passives: SlotView[]): void {
    const key = JSON.stringify([spells, passives]);
    if (key === this.slotKey) return;
    this.slotKey = key;
    const html = (slots: SlotView[], small: boolean) =>
      slots
        .map((s) => `<div class="slot ${small ? 'small' : ''} ${s.evolved ? 'evolved' : ''} ${s.ready ? 'ready' : ''}" style="--c:${s.color}" title="${esc(s.title)}">${s.icon}<span class="lv">${s.level}</span></div>`)
        .join('');
    $('#spell-slots').innerHTML = html(spells, false);
    $('#passive-slots').innerHTML = html(passives, true);
  }

  showBanner(title: string, sub = '', color = '#ffffff'): void {
    this.bannerTitle.textContent = title;
    this.bannerSub.textContent = sub;
    this.banner.style.setProperty('--bc', color);
    this.banner.getAnimations().forEach((a) => a.cancel());
    const frames = reducedMotion()
      ? [
          { opacity: 0, transform: 'translate(-50%, -50%)' },
          { opacity: 1, transform: 'translate(-50%, -50%)', offset: 0.12 },
          { opacity: 1, transform: 'translate(-50%, -50%)', offset: 0.8 },
          { opacity: 0, transform: 'translate(-50%, -50%)' },
        ]
      : [
          { opacity: 0, transform: 'translate(-50%, -40%) scale(0.9)' },
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)', offset: 0.12 },
          { opacity: 1, transform: 'translate(-50%, -50%) scale(1)', offset: 0.8 },
          { opacity: 0, transform: 'translate(-50%, -60%) scale(1)' },
        ];
    this.banner.animate(frames, { duration: 2600, easing: EASE_OUT });
  }

  hurt(): void {
    this.hurtFlash.animate([{ opacity: 0.85 }, { opacity: 0 }], { duration: 320, easing: EASE_OUT });
  }

  flash(opacity = 0.7, duration = 140): void {
    this.screenFlash.animate([{ opacity }, { opacity: 0 }], { duration, easing: EASE_OUT });
  }

  // --- modals ---

  showLevelUp(title: string, sub: string, options: UpgradeOption[], rerolls: number, banishes = 0, locked = false): void {
    $('#levelup-title').textContent = title;
    $('#levelup-sub').textContent = sub;
    const alreadyOpen = !this.levelModal.classList.contains('hidden');
    this.levelCards.classList.toggle('rerolling', alreadyOpen);
    this.levelCards.innerHTML = options.map((o, i) => this.card(o, i, true)).join('');
    this.rerollButton.classList.toggle('hidden', rerolls <= 0);
    this.rerollButton.innerHTML = `${esc(t('levelup.reroll', { n: rerolls }))} <span class="btn-key">[R]</span>`;
    this.banishButton.classList.toggle('hidden', banishes <= 0 || options.length <= 1);
    this.banishButton.innerHTML = `${esc(t('levelup.banish', { n: num(banishes) }))} <span class="btn-key">[B]</span>`;
    this.lockButton.classList.remove('hidden');
    this.lockButton.classList.toggle('on', locked);
    this.lockButton.innerHTML = `${esc(t(locked ? 'levelup.locked' : 'levelup.lock'))} <span class="btn-key">[L]</span>`;
    $('#levelup-actions').querySelector('[data-action="chest-close"]')?.remove();
    this.levelModal.classList.remove('hidden');
    this.picking = true;
    this.levelCards.querySelector<HTMLElement>('.upgrade')?.focus({ preventScroll: true });
  }

  showChest(title: string, sub: string, results: UpgradeOption[]): void {
    $('#levelup-title').textContent = title;
    $('#levelup-sub').textContent = sub;
    this.levelCards.classList.remove('rerolling');
    this.levelCards.innerHTML = results.map((o, i) => this.card(o, i, false)).join('');
    this.rerollButton.classList.add('hidden');
    this.banishButton.classList.add('hidden');
    this.lockButton.classList.add('hidden');
    const actions = $('#levelup-actions');
    actions.querySelector('[data-action="chest-close"]')?.remove();
    actions.insertAdjacentHTML('beforeend', `<button class="btn primary" type="button" data-action="chest-close">${esc(t('common.continue'))}</button>`);
    this.levelModal.classList.remove('hidden');
    this.picking = false;
    actions.querySelector<HTMLElement>('[data-action="chest-close"]')?.focus({ preventScroll: true });
  }

  hideLevelUp(): void {
    this.levelModal.classList.add('hidden');
    this.picking = false;
  }

  private card(o: UpgradeOption, i: number, pickable: boolean): string {
    return `<button type="button" class="upgrade ${pickable ? '' : 'static'}" ${pickable ? `data-pick="${i}"` : 'tabindex="-1"'} style="--c:${o.color}">
      ${pickable ? `<span class="key">${num(i + 1)}</span>` : ''}
      <div class="icon">${o.icon.startsWith('<svg') ? o.icon : `<span>${o.icon}</span>`}</div>
      <div class="name">${esc(o.name)}</div>
      <div><span class="tag ${o.tagClass}">${esc(o.tag)}</span></div>
      <div class="desc">${esc(o.desc)}</div>
      ${o.hint ? `<div class="hint-evo">${esc(o.hint)}</div>` : ''}
    </button>`;
  }

  showPause(html: string): void {
    $('#pause-body').innerHTML = html;
    this.pauseModal.classList.remove('hidden');
    this.pauseModal.querySelector<HTMLElement>('[data-action="resume"]')?.focus({ preventScroll: true });
  }

  hidePause(): void {
    this.pauseModal.classList.add('hidden');
  }

  showResults(title: string, sub: string, html: string, actions: Array<[string, string, boolean]>, victory: boolean): void {
    this.resultsModal.classList.toggle('defeat', !victory);
    $('#results-title').textContent = title;
    $('#results-sub').textContent = sub;
    $('#results-note').textContent = '';
    $('#results-body').innerHTML = html;
    $('#results-actions').innerHTML = actions
      .map(([action, label, primary]) => `<button class="btn ${primary ? 'primary' : 'ghost'}" type="button" data-action="${action}">${esc(label)}</button>`)
      .join('');
    this.resultsModal.classList.remove('hidden');
    this.resultsModal.querySelector<HTMLElement>('.btn.primary')?.focus({ preventScroll: true });
  }

  hideResults(): void {
    this.resultsModal.classList.add('hidden');
  }

  setResultsNote(text: string): void {
    $('#results-note').textContent = text;
  }

  // A short message over whatever is open: the answer to a purchase, or that the server could not be reached.
  toast(text: string): void {
    let el = document.getElementById('toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.append(el);
    }
    el.textContent = text;
    el.classList.add('show');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => el?.classList.remove('show'), 2600);
  }

  // --- account & leaderboard ---

  renderAccountButton(account: Account | null): void {
    const label = $('#account-label');
    label.textContent = account ? account.name : t('menu.account');
    label.toggleAttribute('data-i18n', !account); // a player name must not be overwritten by applyStatic()
    $('#account-button').classList.toggle('signed-in', !!account);
  }

  // account === null re-renders the guest form in the current mode (tab switch).
  showAccount(account: Account | null, state: { error?: string; busy?: boolean; email?: string; name?: string } = {}): void {
    const body = $('#account-body');
    if (account) {
      this.authMode = 'login'; // after signing out, a returning player wants Sign in, not Register
      const error = state.error ? t(AUTH_ERRORS[state.error] ?? 'account.err.generic') : '';
      body.innerHTML = `
        <p class="account-who">${esc(t('account.signedInAs', { name: account.name }))}</p>
        ${account.email ? `<p class="sub account-email" dir="ltr">${esc(account.email)}</p>` : ''}
        ${account.telegram ? `<p class="sub account-email tg-linked">${TG_ICON}<span dir="auto">${esc(account.telegram)}</span></p>` : ''}
        <p class="sub">${esc(t('account.synced'))}</p>
        ${account.telegram ? '' : `<button class="btn tg" type="button" data-action="telegram-link" ${state.busy ? 'disabled' : ''}>${TG_ICON}${esc(t('account.tgConnect'))}</button>
        <p class="sub tg-connect-note">${esc(t('account.tgConnectNote'))}</p>`}
        <p class="form-error" role="alert">${esc(error)}</p>
        <div class="modal-foot"><button class="btn ghost" type="button" data-action="sign-out">${esc(t('account.signOut'))}</button>
        <button class="btn primary" type="button" data-action="account-close">${esc(t('account.close'))}</button></div>`;
    } else {
      const reg = this.authMode === 'register';
      const error = state.error ? t(AUTH_ERRORS[state.error] ?? 'account.err.generic') : '';
      body.innerHTML = `
        <div class="auth-tabs" role="tablist">
          <button type="button" role="tab" class="auth-tab ${reg ? '' : 'on'}" aria-selected="${!reg}" data-auth-mode="login">${esc(t('account.signIn'))}</button>
          <button type="button" role="tab" class="auth-tab ${reg ? 'on' : ''}" aria-selected="${reg}" data-auth-mode="register">${esc(t('account.register'))}</button>
        </div>
        <form id="account-form" class="auth-form" novalidate>
          <label><span>${esc(t('account.email'))}</span><input name="email" type="email" dir="ltr" inputmode="email" autocomplete="email" maxlength="254" required value="${esc(state.email ?? '')}" /></label>
          <label><span>${esc(t('account.password'))}</span><input name="password" type="password" dir="ltr" autocomplete="${reg ? 'new-password' : 'current-password'}" minlength="8" maxlength="128" required /></label>
          ${reg ? `<label><span>${esc(t('account.name'))}</span><input name="name" type="text" autocomplete="nickname" minlength="3" maxlength="20" required value="${esc(state.name ?? '')}" /><small>${esc(t('account.nameHint'))}</small></label>` : ''}
          <p class="form-error" role="alert">${esc(error)}</p>
          <button class="btn primary" type="submit" ${state.busy ? 'disabled' : ''}>${esc(t(reg ? 'account.submitRegister' : 'account.submitSignIn'))}</button>
        </form>
        <div class="auth-or"><span>${esc(t('account.or'))}</span></div>
        <button class="btn tg" type="button" data-action="telegram" ${state.busy ? 'disabled' : ''}>${TG_ICON}${esc(t('account.telegram'))}</button>
        <p class="sub account-note">${esc(t('account.guestNote'))}</p>`;
    }
    this.accountModal.classList.remove('hidden');
    if (!state.busy) body.querySelector<HTMLElement>(state.error ? 'input[name="password"]' : 'input, .btn.primary')?.focus({ preventScroll: true });
  }

  // Telegram sign-in step 2: a link to the bot (opened by the player's tap, so no popup blocker) while Game polls.
  // match: the number to tap in the bot, so a sign-in link sent by someone else cannot be confirmed blind.
  showTelegramWait(url: string, match: number): void {
    $('#account-body').innerHTML = `
      <p class="sub tg-wait">${esc(t('account.tgWaiting'))}</p>
      <p class="tg-match"><span>${esc(t('account.tgMatch'))}</span><b>${esc(num(match, { useGrouping: false }))}</b></p>
      <a class="btn tg" href="${esc(url)}" target="_blank" rel="noopener noreferrer">${TG_ICON}${esc(t('account.tgOpen'))}</a>
      <p class="sub tg-pending" role="status">${esc(t('account.tgPending'))}</p>
      <div class="modal-foot"><button class="btn ghost" type="button" data-action="telegram-cancel">${esc(t('account.tgBack'))}</button></div>`;
    this.accountModal.classList.remove('hidden');
  }

  hideAccount(): void {
    this.accountModal.classList.add('hidden');
  }

  get accountOpen(): boolean {
    return !this.accountModal.classList.contains('hidden');
  }

  renderLeaderboard(stage: string, data: Leaderboard | 'loading' | 'error', signedIn: boolean): void {
    const tabs = [{ id: 'all', name: t('leaderboard.all'), accent: 'var(--gold)' }, { id: 'daily', name: t('leaderboard.daily'), accent: '#ff9a3c' }, ...STAGES.map((s) => ({ id: s.id, name: stageText(s.id).name, accent: s.visuals.accent }))];
    $('#lb-tabs').innerHTML = tabs.map((s) => `<button type="button" role="tab" class="lb-tab ${s.id === stage ? 'on' : ''}" aria-selected="${s.id === stage}" data-lb-stage="${s.id}" style="--c:${s.accent}">${esc(s.name)}</button>`).join('');
    const all = stage === 'all'; // All sums every realm, so it shows realms cleared instead of level
    const body = $('#lb-body');
    const me = $('#lb-me');
    if (data === 'loading' || data === 'error') {
      body.innerHTML = `<p class="sub lb-msg">${esc(t(data === 'loading' ? 'leaderboard.loading' : 'leaderboard.offline'))}</p>`;
      me.textContent = '';
      return;
    }
    body.innerHTML = data.rows.length
      ? `<table class="stats-table lb-table"><thead><tr><th>${esc(t('leaderboard.rank'))}</th><th>${esc(t('leaderboard.player'))}</th><th class="num">${esc(t('leaderboard.kills'))}</th><th class="num">${esc(t('leaderboard.time'))}</th><th class="num">${esc(t(all ? 'leaderboard.cleared' : 'leaderboard.level'))}</th></tr></thead><tbody>
        ${data.rows.map((r) => `<tr class="${r.me ? 'me' : ''} ${r.rank <= 3 ? `top${r.rank}` : ''}"><td><span class="lb-rank">${num(r.rank)}</span></td><td>${r.xp !== undefined ? `<span class="lb-lvl" title="${esc(t('title.level', { n: num(mainLevel(r.xp).level) }))}">${num(mainLevel(r.xp).level)}</span>` : ''}${esc(r.name)}${r.victory ? ' <span class="lb-win" aria-label="victory">♛</span>' : ''}</td><td class="num">${num(r.kills)}</td><td class="num">${formatTime(r.time)}</td><td class="num">${all ? `${num(r.cleared ?? 0)} <span class="lb-win" aria-hidden="true">♛</span>` : num(r.level)}</td></tr>`).join('')}
      </tbody></table>`
      : `<p class="sub lb-msg">${esc(t('leaderboard.empty'))}</p>`;
    me.textContent = data.me ? t('leaderboard.yourBest', { n: data.me.rank }) : signedIn ? '' : t('leaderboard.signInHint');
  }

  get modalOpen(): boolean {
    return !this.levelModal.classList.contains('hidden') || !this.pauseModal.classList.contains('hidden') || !this.resultsModal.classList.contains('hidden') || this.accountOpen || this.gearOpen || this.itemOpen;
  }
}

export function escapeHtml(s: string): string {
  return esc(s);
}
